import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Link, useHistory } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import './Login.scss';
import loginLogo from '../../assets/images/login_logo.svg';
import loginRight from '../../assets/images/login_right.png';
import { AgentIdentity, identify, isErrorBody, getAllConfigFiles } from '../../services/apiParticulierService';
import { ROUTE_B2B_B2C_SELECTOR, ROUTE_MENTIONS_LEGALES } from '../../routing/paths';
import { redirectToB2B, redirectToB2C } from '../redirectTool';
import { ReactComponent as IconLoader } from '../../assets/icons/loader.svg';

interface LoginFormValues {
    agentCode: string;
    agentPassword: string;
}

const Login: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const { push } = useHistory();
    const date = new Date();
    const year = date.getFullYear();

    const [, setCookie] = useCookies();
    const AlertSwal = withReactContent(Swal);

    const [loading, setLoading] = useState(false);

    document.title = 'SOGYS.APP – CHAPEAU';

    const handleLogin = async (values: LoginFormValues): Promise<boolean> => {
        setLoading(true);

        try {
            const agentIdentity: AgentIdentity = await identify(values.agentCode, values.agentPassword);
            const authCookieData = { ...agentIdentity, agentCode: values.agentCode, campagne: undefined };
            setCookie('Auth', JSON.stringify(authCookieData), { path: '/' });

            // load config files now, just after login success !
            await getAllConfigFiles();

            if (agentIdentity.accessB2C && agentIdentity.accessB2B) {
                push(ROUTE_B2B_B2C_SELECTOR);
            } else if (agentIdentity.accessB2C) {
                redirectToB2C();
            } else if (agentIdentity.accessB2B) {
                redirectToB2B();
            }
            
            return false;
        } catch (error) {
            if (isErrorBody(error) && error.errorId === 'errors.blocked_account') {
                await AlertSwal.fire({
                    title: 'Compte désactivé',
                    text: error.message,
                    icon: 'error',
                    confirmButtonText: 'Fermer',
                });
            } else {
                await AlertSwal.fire({
                    title: 'Authentification refusée',
                    text: 'Vos identifiants ne sont pas reconnus, merci de vérifier les éléments saisis.',
                    icon: 'error',
                    confirmButtonText: 'Fermer',
                });
            }
            return true;
        } finally {
            setLoading(false);
        }
    };

    // Forgotten password modal
    const handleForgottenPassword = () => {
        AlertSwal.fire({
            title: 'Réinitialiser votre mot de passe',
            html: (
                <>
                    Pour réinitialiser votre mot de passe, merci de vous rapprocher de votre administrateur.
                    <br />
                    <br />
                    Celui-ci est à votre disposition du Lundi au Vendredi de 9 à 20h au <a href="tel:0826046046">0826 046 046</a> ou par email a&nbsp;
                    <a href="mailto:contact@sogys.fr">contact@sogys.fr</a>.
                </>
            ),
            icon: 'info',
            width: 550,
            confirmButtonText: 'Fermer',
            focusConfirm: true,
        });
    };

    const schema = Yup.object({
        agentCode: Yup.string().min(4).required().label('agentCode'),
        agentPassword: Yup.string().required('Le mot de passe est obligatoire').label('agentPassword'),
    });

    return (
        <div className="container-form">
            <div className="form-sign-in">
                <Formik
                    onSubmit={(values, formikHelpers) => {
                        handleLogin(values).then((reset: boolean) => {
                            if (reset) {
                                formikHelpers.resetForm();
                            }
                        });
                    }}
                    initialValues={{ agentCode: '', agentPassword: '' }}
                    validationSchema={schema}
                >
                    {({ values, handleChange, handleSubmit }) => {
                        return (
                            <form onSubmit={handleSubmit} className="w-100">
                                <div className="container-logo mb-5">
                                    <img src={loginLogo} alt="SOGYS.APP" className="login-logo" />
                                </div>

                                <label htmlFor="agentCode" className="login-label">
                                    Code agent
                                </label>
                                <input
                                    type="text"
                                    id="agentCode"
                                    className="form-control login-input mb-4"
                                    placeholder="Code agent"
                                    required
                                    autoFocus
                                    autoComplete="chrome-off"
                                    onChange={handleChange}
                                    value={values.agentCode || ''}
                                />

                                <label htmlFor="agentPassword" className="login-label">
                                    Mot de passe
                                </label>
                                <input
                                    type="password"
                                    id="agentPassword"
                                    className="form-control login-input mb-5"
                                    placeholder="Mot de passe"
                                    autoComplete="chrome-off"
                                    onChange={handleChange}
                                    value={values.agentPassword || ''}
                                    required
                                />
                                <div className="text-center mb-3">
                                    <button className="btn-login min-width position-relative" type="submit" disabled={loading}>
                                        <span style={loading ? { opacity: '0' } : { opacity: '1' }}>CONNEXION</span>
                                        {loading && (
                                            <div className="login-loader">
                                                <IconLoader width={38} height={38} />
                                            </div>
                                        )}
                                    </button>
                                </div>

                                <div className="text-center">
                                    <button type="button" className="btn-forgot-password" onClick={handleForgottenPassword}>
                                        Mot de Passe oublié
                                    </button>
                                </div>
                            </form>
                        );
                    }}
                </Formik>

                <div className="legal absolute">
                    Tous droits réservés - &copy;{' '}
                    <a href="http://www.sogys.fr" target="_blank" rel="noreferrer">
                        SOGYS
                    </a>{' '}
                    {year === 2021 ? '2021' : `2021 - ${year}`} -{' '}
                    <Link to={ROUTE_MENTIONS_LEGALES} target="_blank">
                        Mentions légales
                    </Link>
                </div>
            </div>
            <div className="login-right-section">
                <img src={loginRight} alt="Audit Énergétique" className="login-right-image" />
            </div>
        </div>
    );
};

export default Login;
